import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  LinearProgress,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Breadcrumbs,
  Link,
} from '@mui/material'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import {
  Add,
  Edit,
  Delete,
  Category as CategoryIcon,
  Close,
  ExpandMore,
  ChevronRight,
  FolderOpen,
  Folder,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { apiClient } from '../services/api'

interface Category {
  id: string
  name: string
  slug: string
  description?: string
  parent_id?: string
  sort_order: number
  is_active: boolean
  meta_title?: string
  meta_description?: string
  meta_keywords?: string
  children?: Category[]
  product_count?: number
}

const CategoriesMUI: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [categoryTree, setCategoryTree] = useState<Category[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list')

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    parent_id: '',
    sort_order: 0,
    is_active: true,
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
  })

  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    loadCategories()
    loadCategoryTree()
  }, [])

  const loadCategories = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/admin/categories', {
        params: { per_page: 100 }
      })
      setCategories(response.data.items || [])
    } catch (error) {
      console.error('Error loading categories:', error)
      enqueueSnackbar('Failed to load categories', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadCategoryTree = async () => {
    try {
      const response = await apiClient.get('/admin/categories/tree')
      setCategoryTree(response.data.tree || [])
    } catch (error) {
      console.error('Error loading category tree:', error)
    }
  }

  const handleOpenDialog = (category?: Category) => {
    if (category) {
      setEditingCategory(category)
      setFormData({
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        parent_id: category.parent_id || '',
        sort_order: category.sort_order,
        is_active: category.is_active,
        meta_title: category.meta_title || '',
        meta_description: category.meta_description || '',
        meta_keywords: category.meta_keywords || '',
      })
    } else {
      setEditingCategory(null)
      setFormData({
        name: '',
        slug: '',
        description: '',
        parent_id: '',
        sort_order: 0,
        is_active: true,
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
      })
    }
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingCategory(null)
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      slug: editingCategory ? formData.slug : generateSlug(name)
    })
  }

  const handleSubmit = async () => {
    setSaving(true)
    try {
      const payload = {
        ...formData,
        parent_id: formData.parent_id || null
      }

      if (editingCategory) {
        await apiClient.put(`/admin/categories/${editingCategory.id}`, payload)
        enqueueSnackbar('Category updated successfully', { variant: 'success' })
      } else {
        await apiClient.post('/admin/categories', payload)
        enqueueSnackbar('Category created successfully', { variant: 'success' })
      }
      handleCloseDialog()
      loadCategories()
      loadCategoryTree()
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || 'Failed to save category',
        { variant: 'error' }
      )
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      try {
        await apiClient.delete(`/admin/categories/${id}`)
        enqueueSnackbar('Category deleted successfully', { variant: 'success' })
        loadCategories()
        loadCategoryTree()
      } catch (error: any) {
        enqueueSnackbar(
          error?.response?.data?.detail || 'Failed to delete category',
          { variant: 'error' }
        )
      }
    }
  }

  const renderTreeItem = (category: Category) => (
    <TreeItem
      key={category.id}
      itemId={category.id}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
          <Typography variant="body1" sx={{ flexGrow: 1 }}>
            {category.name}
          </Typography>
          {category.product_count !== undefined && (
            <Chip
              label={`${category.product_count} products`}
              size="small"
              sx={{ mr: 1 }}
            />
          )}
          <Chip
            label={category.is_active ? 'Active' : 'Inactive'}
            color={category.is_active ? 'success' : 'default'}
            size="small"
            sx={{ mr: 1 }}
          />
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleOpenDialog(category)
            }}
            sx={{ color: '#2196f3' }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleDelete(category.id, category.name)
            }}
            sx={{ color: '#f44336' }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      }
    >
      {category.children && category.children.map(child => renderTreeItem(child))}
    </TreeItem>
  )

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Category Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Organize products in a hierarchical category structure
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={viewMode === 'list' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('list')}
            sx={{ textTransform: 'none' }}
          >
            List View
          </Button>
          <Button
            variant={viewMode === 'tree' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('tree')}
            sx={{ textTransform: 'none' }}
          >
            Tree View
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              bgcolor: '#f44336',
              '&:hover': { bgcolor: '#c62828' },
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 2
            }}
          >
            Add Category
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {!loading && viewMode === 'list' && (
        <Card>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#0d47a1' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Slug</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Parent</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <CategoryIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No categories found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Get started by creating your first category
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        sx={{
                          bgcolor: '#f44336',
                          '&:hover': { bgcolor: '#c62828' }
                        }}
                      >
                        Create First Category
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((category) => (
                    <TableRow key={category.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{category.name}</Typography>
                        {category.description && (
                          <Typography variant="caption" color="text.secondary">
                            {category.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {category.slug}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {category.parent_id ? (
                          <Chip
                            label={categories.find(c => c.id === category.parent_id)?.name || 'Unknown'}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Chip label="Root" size="small" color="primary" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={category.is_active ? 'Active' : 'Inactive'}
                          color={category.is_active ? 'success' : 'default'}
                          size="small"
                          sx={{ borderRadius: 4 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(category)}
                          sx={{ color: '#2196f3' }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(category.id, category.name)}
                          sx={{ color: '#f44336' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {!loading && viewMode === 'tree' && (
        <Card sx={{ p: 3 }}>
          {categoryTree.length === 0 ? (
            <Alert severity="info">
              No categories found. Create your first category to get started.
            </Alert>
          ) : (
            <SimpleTreeView
              defaultExpandedItems={[]}
              sx={{ flexGrow: 1, overflowY: 'auto' }}
            >
              {categoryTree.map(category => renderTreeItem(category))}
            </SimpleTreeView>
          )}
        </Card>
      )}

      {/* Category Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        {saving && <LinearProgress />}
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#2196f3',
          color: 'white'
        }}>
          {editingCategory ? 'Edit Category' : 'Add Category'}
          <IconButton onClick={handleCloseDialog} sx={{ color: 'inherit' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Category Name"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                margin="normal"
                required
                helperText="URL-friendly identifier"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                margin="normal"
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Parent Category</InputLabel>
                <Select
                  value={formData.parent_id}
                  label="Parent Category"
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                >
                  <MenuItem value="">None (Root Category)</MenuItem>
                  {categories
                    .filter(c => !editingCategory || c.id !== editingCategory.id)
                    .map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sort Order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                margin="normal"
                helperText="Lower numbers appear first"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                SEO Settings
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Meta Title"
                value={formData.meta_title}
                onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Meta Description"
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                margin="normal"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Meta Keywords"
                value={formData.meta_keywords}
                onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                margin="normal"
                helperText="Comma-separated keywords"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !formData.name?.trim() || !formData.slug?.trim()}
            sx={{
              bgcolor: '#f44336',
              '&:hover': { bgcolor: '#c62828' }
            }}
          >
            {saving ? 'Saving...' : (editingCategory ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CategoriesMUI
